import { getFirebaseIdToken } from "./anthropic-proxy-client";
import { buildLimitMessage, type UsageAction, type UsageCheckResult } from "./subscription-plans";
import { getSimulatePlan, isSimulatingRegularUser } from "./test-as-user";
import { getUsageSession } from "./usage-session";

async function usageAuthHeaders(): Promise<Record<string, string>> {
  if (getUsageSession()?.isAdmin && !isSimulatingRegularUser()) {
    return { "Content-Type": "application/json" };
  }
  const token = await getFirebaseIdToken();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

function usageBody(action: UsageAction, extra: Record<string, unknown> = {}) {
  const simulate = getSimulatePlan();
  return {
    action,
    ...(simulate
      ? { simulateAsUser: true, simulatePlan: simulate }
      : {}),
    ...extra,
  };
}

async function postJson<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const headers = await usageAuthHeaders();
  const response = await fetch(path, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(
      (payload as { message?: string; error?: string })?.message ||
        (payload as { error?: string })?.error ||
        `Usage API ${response.status}`,
    );
    (err as Error & { usage?: UsageCheckResult }).usage = payload as UsageCheckResult;
    throw err;
  }
  return payload as T;
}

/** Backend-first quota check. Throws if not allowed. */
export async function enforceUsage(
  action: UsageAction,
  resourceId?: string,
): Promise<UsageCheckResult> {
  if (!getUsageSession()?.userId) {
    throw new Error("Log ind for at fortsætte.");
  }
  if (getUsageSession()?.isAdmin && !isSimulatingRegularUser()) {
    return {
      allowed: true,
      action,
      planId: "pro",
      currentUsage: 0,
      limit: 999,
      remaining: 999,
      resetsAt: new Date().toISOString(),
      bypass: true,
    };
  }

  try {
    return await postJson<UsageCheckResult>(
      "/api/usage/enforce",
      usageBody(action, { resourceId }),
    );
  } catch (error) {
    if (import.meta.env.DEV && import.meta.env.VITE_USAGE_ENFORCE !== "true") {
      return {
        allowed: true,
        action,
        planId: "pro",
        currentUsage: 0,
        limit: 999,
        remaining: 999,
        resetsAt: new Date().toISOString(),
        bypass: true,
      };
    }
    const session = getUsageSession();
    const lang = session?.lang === "EN" ? "EN" : "DA";
    const usage = (error as Error & { usage?: UsageCheckResult }).usage;
    if (usage && !usage.allowed) {
      const limitErr = new Error(usage.message || buildLimitMessage(action, usage, lang));
      limitErr.cause = error;
      throw limitErr;
    }
    const message = error instanceof Error ? error.message : String(error);
    if (/Usage API 5\d\d|Invalid path|fetch failed|Failed to fetch|network|401/i.test(message)) {
      return {
        allowed: true,
        action,
        planId: "pro",
        currentUsage: 0,
        limit: 999,
        remaining: 999,
        resetsAt: new Date().toISOString(),
        bypass: true,
      };
    }
    throw error;
  }
}

export const USAGE_UPDATED_EVENT = "bbg-usage-updated";

export function notifyUsageUpdated(detail?: { action?: UsageAction }) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(USAGE_UPDATED_EVENT, { detail }));
}

function adminBypassResult(action: UsageAction): UsageCheckResult {
  return {
    allowed: true,
    action,
    planId: "pro",
    currentUsage: 0,
    limit: 999,
    remaining: 999,
    resetsAt: new Date().toISOString(),
    bypass: true,
  };
}

/** Read current quota without recording (never throws on 403 — returns full status). */
export async function fetchUsageStatus(action: UsageAction): Promise<UsageCheckResult | null> {
  if (!getUsageSession()?.userId) return null;
  if (getUsageSession()?.isAdmin && !isSimulatingRegularUser()) {
    return adminBypassResult(action);
  }

  try {
    return await postJson<UsageCheckResult>("/api/usage/enforce", usageBody(action));
  } catch (error) {
    if (import.meta.env.DEV && import.meta.env.VITE_USAGE_ENFORCE !== "true") {
      return adminBypassResult(action);
    }
    const usage = (error as Error & { usage?: UsageCheckResult }).usage;
    if (usage) return usage;
    return null;
  }
}

export async function fetchLitraUsageStatus(): Promise<UsageCheckResult | null> {
  return fetchUsageStatus("litra_prompt_used");
}

/** Record usage after a successful action (idempotent per resourceId for books). */
export async function recordUsage(
  action: UsageAction,
  resourceId?: string,
): Promise<UsageCheckResult | null> {
  if (!getUsageSession()?.userId || (getUsageSession()?.isAdmin && !isSimulatingRegularUser())) {
    return null;
  }

  try {
    const result = await postJson<UsageCheckResult>(
      "/api/usage/record",
      usageBody(action, { resourceId }),
    );
    notifyUsageUpdated({ action });
    return result;
  } catch {
    return null;
  }
}

/** Gate + throw on deny. */
export async function usageGate(action: UsageAction, resourceId?: string): Promise<void> {
  await enforceUsage(action, resourceId);
}

export async function signupUsageCheck(
  userId: string,
  email: string,
  fingerprint: string,
): Promise<void> {
  const response = await fetch("/api/usage/signup-check", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId, email, fingerprint }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.allowed === false) {
    throw new Error(
      payload?.message ||
        "Denne konto kan ikke oprettes. Kontakt support hvis du mener det er en fejl.",
    );
  }
}
